/* eslint-disable @typescript-eslint/naming-convention */
import React, { memo, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Grid, FormControlLabel } from "@mui/material";
import { Data } from "../../../store/receipt/types";
import { saveRequest as saveReceipt } from "../../../store/receipt/actions";
import { fetchRequest as fetchCardTypes } from "../../../store/cardType/actions";
import { Formik, Form, FormikProps } from "formik";
import { Editor } from "@tinymce/tinymce-react";

import pageStyles from "../../pageStyles";
import FormSubmitButtons from "../../../common/buttons/FormSubmitButtons";
import SectionWrapper from "../../../common/pageSections/SectionWrapper";
import TextInput from "../../../common/formElements/TextInput";
import schema from "./ReceiptForm.schema";
import SelectDropDown from "../../../common/formElements/SelectDropDown";
import CustomizedSwitch from "../../../common/formElements/Switch";
import ProgressIndicator from "../../../common/progressIndicator/ProgressIndicator";
import MultipleSelect from "../../../common/formElements/MultipleSelectDropDown";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { useToastMessage } from "../../../effects/useToastMessage";
import { toast } from "react-toastify";
// import CustomizedSwitch from "../../../common/formElements/Switch";

interface ReceiptEditForm {
  receiptData: Data | null;
  cardTypes: any;
  isCreate: boolean;
}

const receiptsFor = ["Merchant", "Customer"];

// const Types = [
//   "HTML",
//   "Items"
// ]

function ReceiptEditForm({
  receiptData,
  cardTypes,
  isCreate,
}: ReceiptEditForm) {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = pageStyles();
  const [isEditorLoading, setIsEditorLoading] = useState(true);

  const cardTypeStateLoading = useSelector(
    (state: ApplicationState) => state.cardType.loading ?? []
  );

  useEffect(() => {
    dispatch(fetchCardTypes(null));
  }, []);

  const initialValues = receiptData || {
    id: null,
    type: "Receipt",
    attributes: {
      Name: "",
      For: "Customer",
      Items: [],
      CardType: [],
      Default: false,
      HTML: "",
      Type: "HTML",
    },
  };

  return cardTypeStateLoading ? (
    <ProgressIndicator />
  ) : (
    <Formik
      enableReinitialize={true}
      validationSchema={schema}
      initialValues={initialValues}
      onSubmit={(values) => {
        const callback = (err: string | null) => {
          if (err) {
            console.log(err);
            let errText = `An error occurred while saving: ${err}`;
            toast.error(errText, {
              closeOnClick: true,
            });
          } else {
            if (isCreate) {
              history.goBack();
            } else {
              toast.success("Saved successfully!", {
                closeOnClick: true,
              });
            }
          }
        };
        dispatch(saveReceipt(values, callback));
      }}
    >
      {(props: FormikProps<Data>) => {
        const { values, setFieldValue, isSubmitting, errors } = props;

        // Show toast errors
        useToastMessage(isSubmitting, errors);

        const onChangeHtml = (newValue: string) => {
          setFieldValue("attributes.HTML", newValue);
        };
        return (
          <Form>
            <SectionWrapper title="Receipt">
              <Grid
                container
                alignItems="flex-start"
                spacing={2}
                className={classes.formWrapper as string}
              >
                <Grid item xs={12} md={6}>
                  <TextInput fieldname="attributes.Name" label="Name" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SelectDropDown
                    fieldname="attributes.For"
                    label="For"
                    options={receiptsFor}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MultipleSelect
                    multiple={true}
                    fieldname="attributes.CardType"
                    label="Card Types"
                    options={cardTypes.map((i: any) => i.type)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    style={{ marginLeft: "5px" }}
                    control={
                      <CustomizedSwitch
                        name="attributes.Default"
                        checked={values?.attributes?.Default || false}
                        onChange={(event, value) => {
                          setFieldValue("attributes.Default", value);
                        }}
                      />
                    }
                    label="Default"
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  {isEditorLoading && <ProgressIndicator />}
                  <Editor
                    onPostRender={() => {
                      setIsEditorLoading(false);
                    }}
                    apiKey={process.env.TINYMCE_API_KEY}
                    // onInit={(evt, editor) => (editorRef = editor)}
                    onEditorChange={(newValue) => onChangeHtml(newValue)}
                    initialValue={initialValues.attributes.HTML}
                    init={{
                      height: 500,
                      width: "100%",
                      menubar: false,
                      element_format: "html",
                      extended_valid_elements:
                        "forloop[destination|source], visibilitycheck[target|attribute]",
                      valid_elements:
                        "div[class|id], p[class|id], visibilitycheck[targetId|attribute], forloop[destination|source]",
                      valid_children: "+body[style]",
                      verify_html: false,
                      plugins: [
                        "advlist",
                        "autolink",
                        "lists",
                        "link",
                        "image",
                        "charmap",
                        "preview",
                        "anchor",
                        "searchreplace",
                        "visualblocks",
                        "fullscreen",
                        "insertdatetime",
                        "media",
                        "table",
                        "help",
                        "wordcount",
                        "code",
                      ],
                      toolbar:
                        "undo redo | casechange blocks | bold italic backcolor | " +
                        "alignleft aligncenter alignright alignjustify | " +
                        "bullist numlist checklist outdent indent | removeformat | a11ycheck code table help",
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      content_style:
                        ".receipt-container { width: 650px; } .text-container { display: flex; justify-content: space-between; } .receipt-logo { display: flex; flex-direction: column; align-items: center; } .receipt-header { display: flex; flex-direction: column; align-items: center; } .receipt-bottom div { width: auto; text-align: center; } .receipt-text { color: #000000; font-family: monospace; font-size: 20px; text-transform: uppercase; margin: 0px; } .lines { margin: 0px; display: flex; justify-content: center; color: #000000; font-family: monospace; font-size: 20px; } .invisible { display: none; } .center { text-align: center; padding: 20px 0px; font-size: 24px;}",
                    }}
                  />
                </Grid>
                <FormSubmitButtons />
              </Grid>
            </SectionWrapper>
          </Form>
        );
      }}
    </Formik>
  );
}
export default memo(ReceiptEditForm);
